import type { Car } from '../cars/types';
import type { Customer, PaymentMethod } from '../customers/types';
import type { Subscription } from '../subscriptions/types';

export type AdditionalDriver = Customer & {
  status?: string;
  driverType?: 'Free' | 'Paid';
  feeAmount?: number | null;
};

export type AdditionalDriverPricing = {
  dailyRate: number;
  days: number;
  freeDrivers: number;
  nextDriverType: 'Free' | 'Paid';
  nextFeeAmount: number | null;
};

export type ChargingRecord = {
  id: number;
  record_id: string;
  name: string;
  date: string;
  amount: string;
  startTimestamp: string;
};

export type Checkin = {
  id: number;
  reservation: Reservation;
  isComplete: boolean;
  tutorialCompleted: boolean;
  licenseVerified: boolean;
};

export type Reservation = {
  id: number;
  startTime: string;
  endTime: string;
  pickupLocation: RentalLocation;
  returnLocation: RentalLocation;
  status: string;
  outstandingBalance: number;
  car: Car;
  reservationRates: ReservationRate[];
  extras: Extra[];
  numDays: number;
  insurance: Insurance;
  cancellationPolicy: CancellationPolicy;
  insuranceCost: number;
  resolvedChargingAmount: number;
  chargingUsed: number;
  chargingUsedWithUpcharge: number;
  passThroughCharging: boolean;
  incidentalsCost: number;
  resolvedTollsAmount: number;
  tollsUsedWithUpcharge: number;
  ownerTollsAmount: number;
  passThroughTolls: boolean;
  payments: Payment[];
  subtotal: number;
  salesTax: number;
  adjustments: number;
  totalCost: number;
  tolls: Toll[];
  extraMiles: number;
  additionalMilesCost: number;
  incidentals: Incidental[];
  customPickupLocation: string;
  customReturnLocation: string;
  pickupLocationLatitude: number;
  pickupLocationLongitude: number;
  returnLocationLatitude: number;
  returnLocationLongitude: number;
  totalDeliveryCost: number;
  customer: Customer;
  holdAmount: number;
  startingMileage: number;
  endingMileage: number;
  chargingRecords: ChargingRecord[];
  contractSignedAt: string;
  contractInitialData: string;
  contractSignatureData: string;
  discounts: Discount[];
  giftCodes: GiftCode[];
  manualDiscount: number;
  ownerDiscount: number;
  mileagePlan: MileagePlan;
  initiationFee: number;
  refunds: Refund[];
  reservationAdjustments: ReservationAdjustment[];
  stripeHoldIntentId: string;
  capturedHold: boolean;
  isCheckinComplete: boolean;
  checkin: Checkin;
  carImages: Image[];
  preTripCarImages: Image[];
  postTripCarImages: Image[];
  checkinPin: string;
  gloveboxPin: string;
  sendToCollections: boolean;
  sentToCollectionsAt: string;
  review: Review;
  customerMarkedFinishedAt: boolean;
  cdwEnabled: boolean;
  rcliEnabled: boolean;
  controlPanelState: 'enabled' | 'disabled' | 'unavailable';
  mileagePlanCost: number;
  subscription: Subscription;
  additionalDrivers: AdditionalDriver[];
  additionalDriverPricing: AdditionalDriverPricing;
  sfCarKeyLink: string;
  sfCarWebKeyLink: string;
  paidGlovebox: boolean;
  acceptedCarConditionsAt: string;
  weeklyDiscount: number;
  monthlyDiscount: number;
  ownerEarnings: number;
  hostNotes: string;
  isBlock: boolean;
  showJooserAdvert: boolean;
  totalCancellationPolicyCost: number;
  createdAt: string;
  originalCard: PaymentMethod;
  ownerId: number | null;
  // Present on the internal partners reservations list; not guaranteed on
  // guest-facing reservation responses.
  source?: ReservationSource;
  customerType?: CustomerType;
  // Present on internal partners reservation endpoints only.
  isPickedUp?: boolean;
  autopayDatetime: string;
  lockedOutAt: string;
  lockoutDatetime: string | null;
};

export type ReservationSource = 'eon_one' | 'eon';

export type CustomerType = 'eon' | 'eon_via_portal' | 'direct';

export type ContractSign = Reservation & {
  stripeClientSecret: string | null;
};

export type DailyHostEarningsBreakdown = {
  baseRental: number;
  dailyRate: number;
  numDays: number;
  weeklyDiscount: number;
  monthlyDiscount: number;
  ownerEarningsPercent: number;
  rentalEarnings: number;
  delivery: number;
  charging: number;
  tolls: number;
  extraMiles: number;
  ownerDiscount: number;
  total: number;
};

export type SubscriptionHostEarningsBreakdown = {
  baseEarnings: number;
  renewalPeriod: 'weekly' | 'monthly' | '6_months';
  mileageTier: number | 'Unlimited';
  delivery: number;
  charging: number;
  tolls: number;
  ownerDiscount: number;
  total: number;
};

export type HostEarningsBreakdown =
  | DailyHostEarningsBreakdown
  | SubscriptionHostEarningsBreakdown;

export const isDailyHostEarningsBreakdown = (
  breakdown: HostEarningsBreakdown
): breakdown is DailyHostEarningsBreakdown => 'baseRental' in breakdown;

export type AdminReservation = {
  id: number;
  startTime: string;
  endTime: string;
  pickupLocation: RentalLocation;
  returnLocation: RentalLocation;
  status: string;
  outstandingBalance: number;
  car: Car;
  reservationRates: ReservationRate[];
  extras: Extra[];
  numDays: number;
  insurance: Insurance;
  insuranceCost: number;
  resolvedChargingAmount: number;
  chargingUsed: number;
  chargingUsedWithUpcharge: number;
  passThroughCharging: boolean;
  incidentalsCost: number;
  resolvedTollsAmount: number;
  tollsUsedWithUpcharge: number;
  ownerTollsAmount: number;
  passThroughTolls: boolean;
  payments: Payment[];
  subtotal: number;
  salesTax: number;
  adjustments: number;
  totalCost: number;
  tolls: Toll[];
  extraMiles: number;
  additionalMilesCost: number;
  incidentals: Incidental[];
  customPickupLocation: string;
  customReturnLocation: string;
  pickupLocationLatitude: number;
  pickupLocationLongitude: number;
  returnLocationLatitude: number;
  returnLocationLongitude: number;
  totalDeliveryCost: number;
  customerName: string;
  customerPhone: string;
  holdAmount: number;
  startingMileage: number;
  endingMileage: number;
  chargingRecords: ChargingRecord[];
  contractSignedAt: string;
  contractInitialData: string;
  contractSignatureData: string;
  discounts: Discount[];
  giftCodes: GiftCode[];
  manualDiscount: number;
  ownerDiscount: number;
  mileagePlan: MileagePlan;
  initiationFee: number;
  refunds: Refund[];
  reservationAdjustments: ReservationAdjustment[];
  stripeHoldIntentId: string;
  capturedHold: boolean;
  isCheckinComplete: boolean;
  checkin: Checkin;
  carImages: Image[];
  preTripCarImages: Image[];
  postTripCarImages: Image[];
  checkinPin: string;
  gloveboxPin: string;
  sendToCollections: boolean;
  sentToCollectionsAt: string;
  review: Review;
  customerMarkedFinishedAt: boolean;
  cdwEnabled: boolean;
  rcliEnabled: boolean;
  controlPanelState: 'enabled' | 'disabled' | 'unavailable';
  mileagePlanCost: number;
  subscription: Subscription;
  additionalDrivers: AdditionalDriver[];
  additionalDriverPricing: AdditionalDriverPricing;
  sfCarKeyLink: string;
  sfCarWebKeyLink: string;
  paidGlovebox: boolean;
  acceptedCarConditionsAt: string;
  weeklyDiscount: number;
  monthlyDiscount: number;
  ownerWeeklyDiscount: number;
  ownerMonthlyDiscount: number;
  ownerManualDiscount: number;
  ownerDiscountEarningsImpact: number;
  ownerEarnings: number;
  ownerDeliveryPayout: number;
  ownerAdditionalMilesCost: number;
  hostEarningsBreakdown: HostEarningsBreakdown | null;
  hostNotes: string;
  isBlock: boolean;
  managementCommissionPercentage: number;
  customerType?: CustomerType;
  chargingRecordImages: string[];
  tollImages: string[];
};

export type Review = {
  score: number;
  feedback: string;
};

export type Image = {
  id: number;
  createdAt: string;
  photo: string;
  userName: string;
};

export type GiftCode = {
  code: string;
  amount: number;
};

export type RentalLocation = {
  id: number;
  city: string;
  state: string;
  address: string;
  zipcode: string;
  latitude: number;
  longitude: number;
  timezone: string;
  pickupInstructions: string;
  dropoffInstructions: string;
  garageOpenHour: string;
  garageCloseHour: string;
  carsCount: number;
  streetNumber?: string;
  streetName?: string;
  addressLine2?: string;
  county?: string;
  country?: string;
};

export type ReservationRate = {
  id: number;
  dailyRate: number;
  numDays: number;
  discountAmount: number;
  discountType: string;
  ownerEarnings: number;
  ownerDiscountAmount: number;
};

export type Extra = {
  id: number;
  title: string;
  description: string;
  amount: number;
  payoutAmount: number | null;
  recurring: boolean;
  tag: string;
};

export type Insurance = {
  id: number;
  tag: string;
  category: string;
  // Add-ons/subscription-extras options identify themselves via `name`
  // instead of `category` — see InsuranceCardItem's `insurance.name ||
  // insurance.category` fallback for the existing precedent.
  name?: string;
  description: string;
  minidescription: string;
  percentage: number;
  fixedAmount: number | null;
  meta: any;
};

export type CancellationPolicyComparisonRow = {
  key: string;
  label: string;
  value: string;
};

export type CancellationPolicy = {
  id: number;
  title: string;
  tag: string;
  description: string;
  percentage: number;
  cancelUntil: string;
  sortIndex: number;
  meta?: {
    comparisonRows?: CancellationPolicyComparisonRow[];
  };
};

export type Discount = {
  // id: number;
  // description: string;
  title: string;
  code: string;
  amount: number;
  endDate?: string;
  meta: any;
};

export type Payment = {
  id: number;
  amount: number;
  memo: string;
  isHold: boolean;
  brand: string;
  last4: string;
  walletType: string;
};

export type Toll = {
  id: number;
  name: string;
  date: string;
  amount: string;
  adminAmount: string;
  adminFee: number;
  startTimestamp: string;
  endTimestamp: string;
  startName: string;
  endName: string;
};

export enum IncidentalCategory {
  PARKING_TICKET = 'parking_ticket',
  PARKING_RECEIPT = 'parking_receipt',
  SPEEDING_TICKET = 'speeding_ticket',
  SMOKING = 'smoking',
  PET_HAIR = 'pet_hair',
  GLASS = 'glass',
  FLAT_TIRE = 'flat_tire',
  SCRATCHED_RIMS = 'scratched_rims',
  ACCIDENT_BODY_DAMAGE = 'accident_body_damage',
  INTERIOR_DAMAGE = 'interior_damage',
}

export type Incidental = {
  id: number;
  category: IncidentalCategory;
  amount: number;
  amountWithUpcharge: number;
  payoutAmount: number | null;
  adminFee: number;
  offenseDate: string;
  images: string[];
  status: string;
  isPaid: boolean;
  isPaidAt: string | null;
  claimOwnerResponse: string;
  approvedForBilling: boolean;
};

export type ReservationAdjustment = {
  id: number;
  adjustmentType: string;
  amount: number;
  description: string | null;
};

export type Refund = {
  memo: string;
  amount: number;
};

export type MileagePlan = {
  costPerMiles: number;
  dailyMilesAllowance: number;
  totalMilesAllowance: number;
  unlimited: boolean;
};

export type Expense = {
  id: number;
  amount: number;
  title: string;
  quantity: number;
  totalCost: number;
};

export type OwnerCancellation = {
  id: number;
  reservationId: number;
  ownerId: number;
  createdAt: string;
  penaltyAmount: number;
};

export type Owner = {
  id: number;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  dob: string;
  setupComplete: boolean;
  isSuperadmin: boolean;
  isHost: boolean;
  isFullyManaged: boolean;
  referralCode: string;
  credits: number;
  publicToken: string;
  isCorporate: boolean;
};

export type Payout = {
  id: number;
  owner: Owner;
  startTime: string;
  endTime: string;
  memo: string;
  hostId: number;
  totalHostEarnings: number;
  earningsMinusExpenses: number;
  paymentStatus:
    | 'paid'
    | 'partial_paid'
    | 'pending'
    | 'failed'
    | 'unpaid'
    | null;
  paymentDate: string | null;
  reservations: AdminReservation[];
  expenses: Expense[];
  ownerCancellations: OwnerCancellation[];
};
